// hooks.ts
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useEffect, useState } from "react";
import {
  archiveTemplate,
  changeTemplateStatus,
  cloneBilltemplate,
  deleteTemplate,
  downloadBillTemplate,
  getCanvasComponents,
  getCanvasTemplateDetail,
  getTemplateActivityLog,
  getTemplateChoices,
  getTemplateDetail,
  getTemplateList,
  getTemplateOptions,
  getTemplateSummary,
  getTemplateUsage,
  saveCanvasTemplate,
  updateCanvasTemplate,
} from "./api";

export interface UseCanvasTemplateReturn {
  components: CanvasComponent[];
  setComponents: (components: CanvasComponent[]) => void;
  availableComponents: any[];
  availableTemplates: any[];
  templateName: string;
  setTemplateName: (name: string) => void;
  templateType: string;
  setTemplateType: (type: string) => void;
  category: string;
  setCategory: (category: string) => void;
  perforationEnabled: boolean;
  setPerforationEnabled: (enabled: boolean) => void;
  perforationY: number;
  setPerforationY: (y: number) => void;
  isDoubleSided: boolean;
  setIsDoubleSided: (value: boolean) => void;
  isLoading: boolean;
  isLoadingDetail: boolean;
  queryErrorDetail: Error | null;
  error: string | null;
  saveTemplate: (payload?: any) => Promise<void>;
  isSaving: boolean;
  updateTemplate: (payload?: any) => Promise<void>;
  isUpdating: boolean;
  templateId: number | null;
  baseTemplateId: number | null;
  refetch: () => void;
  loadTemplate: () => void;
  componentConfig: ComponentConfiguration;
  setComponentConfig: (config: ComponentConfiguration) => void;
  isLoadingBaseTemplate: boolean;
  activeSide: "front" | "back";
  setActiveSide: (side: "front" | "back") => void;
  rawDetailData: any;
}

export function useCanvasTemplate(id?: string): UseCanvasTemplateReturn {
  const [components, setComponents] = useState<CanvasComponent[]>([]);
  const [availableComponents, setAvailableComponents] = useState<any[]>([]);
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [templateType, setTemplateType] = useState("");
  const [category, setCategory] = useState("");
  const [perforationEnabled, setPerforationEnabled] = useState(false);
  const [perforationY, setPerforationY] = useState(0);
  const [isDoubleSided, setIsDoubleSided] = useState(false);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [baseTemplateId, setBaseTemplateId] = useState<number | null>(null);
  const [componentConfig, setComponentConfig] =
    useState<ComponentConfiguration>({});
  const [isLoadingBaseTemplate, setIsLoadingBaseTemplate] = useState(false);
  const [activeSide, setActiveSide] = useState<"front" | "back">("front");

  // Fetch available templates or configured template
  const {
    data: templateData,
    isPending: isLoading,
    error: queryError,
    refetch,
  } = useSmartQuery(
    [QueryKeyFactory.module.bx.canvasTemplate.template()],
    () => {
      // New template - get available base templates
      return getTemplateOptions();
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: true,
    },
  );

  const {
    data: templateDataDetail,
    isFetching: isLoadingDetail,
    error: queryErrorDetail,
  } = useSmartQuery(
    QueryKeyFactory.module.bx.canvasTemplate.details(id),
    () => {
      return getCanvasTemplateDetail(id);
    },
    {
      enabled: !!id,
    },
  );

  // Save mutation
  const {
    mutateAsync: saveTemplateMutation,
    isPending: isSaving,
    error: saveError,
  } = useSmartMutation(saveCanvasTemplate);

  // Update mutation
  const {
    mutateAsync: updateTemplateMutation,
    isPending: isUpdating,
    error: updateError,
  } = useSmartMutation(
    ({ id, payload }: { id: string; payload: any }) =>
      updateCanvasTemplate(id, payload),
    {
      onSuccess: () => {
        console.log("debug temp successs");
        queryClient.invalidateQueries({ queryKey: ["bx", "billing", "list"] });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.bx.canvasTemplate.details(id),
        });
      },
    },
  );

  // Load specific base template when user selects from dropdown
  const loadTemplate = async () => {
    try {
      setIsLoadingBaseTemplate(true);
      setComponents([]); // Clear existing components
      setComponentConfig({}); // Clear existing config

      // Fetch the base template with its components
      const data = await getCanvasComponents();
      if (data?.result) {
        processBaseTemplateData(data.result);
      }
    } catch (error) {
      console.error("Error loading template:", error);
    } finally {
      setIsLoadingBaseTemplate(false);
    }
  };

  // Process base template data (for new templates)
  const processBaseTemplateData = (result: any) => {
    // Set the base template ID for saving later
    setBaseTemplateId(result.id);

    // Set available components from the base template
    if (Array.isArray(result)) {
      setAvailableComponents(result);
    } else if ((result as any).components) {
      setAvailableComponents((result as any).components);
    }

    // Load default layout if available
    const layout = (result as any).defaultLayout;
    if (layout && Object.keys(layout).length > 0) {
      const defaultComponents: CanvasComponent[] = [];
      const defaultConfig = (result as any).defaultConfig || {};

      // Create components from default layout
      for (const [key, layoutEntry] of Object.entries(layout)) {
        const layoutData = layoutEntry as any;
        const componentsList = Array.isArray(result)
          ? result
          : (result as any).components;
        const componentDef = componentsList?.find(
          (c: any) => c.id === layoutData.componentId,
        );

        if (componentDef) {
          const config = defaultConfig[key] || {};

          defaultComponents.push({
            id: key,
            componentId: layoutData.componentId,
            type: componentDef.componentName,
            x: layoutData.x || 0,
            y: layoutData.y || 0,
            width: layoutData.width || componentDef.defaultSize?.width || 300,
            height:
              layoutData.height || componentDef.defaultSize?.height || 150,
            zIndex: layoutData.zIndex || 1,
            rotation: layoutData.rotation || 0,
            isVisible: true,
            isLocked: false,
            content: componentDef,
            style: config.style || {
              backgroundColor: "#ffffff",
              borderColor: "#e5e7eb",
              borderWidth: 1,
              borderStyle: "solid",
              borderRadius: 0,
              padding: 10,
              fontSize: 14,
              color: "#374151",
              textAlign: "left",
            },
          });
        }
      }

      setComponents(defaultComponents);
      setComponentConfig(defaultConfig);
    } else {
      // No default layout - start with empty canvas
      setComponents([]);
      setComponentConfig({});
    }

    // Keep template name empty for user to fill
    if (!templateName) {
      setTemplateName("");
    }
  };

  // Process configured template data (for editing existing template)
  const processConfiguredTemplateData = (result: any) => {
    if (!result) return;

    // Set template details
    setTemplateName(
      result?.templateName || result?.configured_template_name || "",
    );
    setTemplateId(result?.id || null);

    // Patch category and type
    setCategory(result?.category || "");
    setTemplateType(result?.template_type || result?.templateType || "");

    // Patch double sided
    const backPage = result?.pages?.back;
    const hasBackData =
      backPage &&
      ((backPage.layout && Object.keys(backPage.layout).length > 0) ||
        (backPage.component_config &&
          Object.keys(backPage.component_config).length > 0) ||
        (backPage.canvas_layout &&
          Object.keys(backPage.canvas_layout).length > 0) ||
        (backPage.componentConfig &&
          Object.keys(backPage.componentConfig).length > 0));
    setIsDoubleSided(
      !!hasBackData || !!result?.is_double_sided || !!result?.isDoubleSided,
    );

    // Patch perforation line
    const perfLine = result?.perforation_line || result?.perforationLine;
    if (perfLine) {
      setPerforationEnabled(!!perfLine.enabled);
      setPerforationY(perfLine.y || perfLine.height || 0);
    }

    // Handle the new nested structure or fallback to old one
    const baseId =
      result?.base_template ||
      result?.baseTemplate ||
      (result?.id < 100 ? result.id : 19);
    setBaseTemplateId(baseId);

    // Fetch available components for the library
    getCanvasComponents()
      .then((baseData) => {
        const baseResult = (baseData as any)?.result || baseData;
        if (baseResult) {
          const componentsList = Array.isArray(baseResult)
            ? baseResult
            : baseResult.components || baseResult;
          if (componentsList) {
            setAvailableComponents(
              Array.isArray(componentsList) ? componentsList : [],
            );

            // Process the canvas layout based on active side
            processCanvasLayout(result, componentsList);
          }
        }
      })
      .catch((error) => {
        console.error("Error loading base template components:", error);
      });
  };

  // Process canvas layout for configured template
  const processCanvasLayout = (
    result: any,
    baseComponents: any[],
    side: "front" | "back" = activeSide,
  ) => {
    // Support for multiple structure formats
    let layoutSource = null;
    let configSource = null;

    if (result?.pages) {
      // New format: result.pages.front/back.layout
      const sideData =
        side === "front" ? result.pages.front : result.pages.back;
      layoutSource = sideData?.layout;
      configSource = sideData?.componentConfig;
    } else {
      // Legacy format
      layoutSource =
        result.configured_sequence ||
        result.canvasLayout ||
        result.defaultConfig;
      configSource = result.configuration_json || result.componentConfig;
    }

    if (layoutSource) {
      const canvasComponents: CanvasComponent[] = [];
      const entries = Array.isArray(layoutSource)
        ? layoutSource.map((item, idx) => [item.id || `comp-${idx}`, item])
        : Object.entries(layoutSource);

      for (const [key, layout] of entries) {
        const layoutData = layout as any;
        // API uses componentAlias (camelCase) or component_alias (snake_case)
        const alias =
          layoutData.componentAlias ||
          layoutData.component_alias ||
          layoutData.alias;

        // Find component definition from base components
        const componentDef = baseComponents.find(
          (c: any) =>
            (layoutData.componentId && c.id === layoutData.componentId) ||
            (alias && c.componentAlias === alias),
        );

        if (componentDef) {
          // Normalize alias lookup for config (API might use camelCase like 'invoiceDetails' for 'invoice_details')
          const normalizedAlias = alias?.replace(/_([a-z])/g, (g) =>
            g[1].toUpperCase(),
          );
          const snakeAlias = alias?.replace(
            /([A-Z])/g,
            (g) => `_${g[0].toLowerCase()}`,
          );

          const sideConfig =
            configSource?.[alias] ||
            configSource?.[normalizedAlias] ||
            configSource?.[snakeAlias] ||
            configSource?.[key] ||
            {};

          // Merge field configuration (handle both snake_case and camelCase from API)
          let fields = [...(componentDef.fields || [])];

          const fieldVisibility =
            sideConfig.field_visibility || sideConfig.fieldVisibility;
          const fieldLabels = sideConfig.field_labels || sideConfig.fieldLabels;
          const fieldOrder = sideConfig.field_order || sideConfig.fieldOrder;

          if (fieldVisibility) {
            fields = fields.map((f) => ({
              ...f,
              isActive: fieldVisibility[f.fieldName] !== false,
            }));
          }

          if (fieldLabels) {
            fields = fields.map((f) => ({
              ...f,
              label: fieldLabels[f.fieldName] || f.label,
            }));
          }

          if (fieldOrder && Array.isArray(fieldOrder)) {
            fields = fields.sort((a, b) => {
              const orderA = fieldOrder.indexOf(a.fieldName);
              const orderB = fieldOrder.indexOf(b.fieldName);
              if (orderA > -1 && orderB > -1) return orderA - orderB;
              if (orderA > -1) return -1;
              if (orderB > -1) return 1;
              return 0;
            });
          }

            // Handle Graph Config if present
            const rawGraphConfig =
              sideConfig.graph_config || sideConfig.graphConfig;
            let graphConfig: GraphConfig | undefined = undefined;
            if (rawGraphConfig) {
              graphConfig = {
                utilityService:
                  rawGraphConfig.utility_service ||
                  rawGraphConfig.utilityService ||
                  "Gas",
                numberOfMonths:
                  rawGraphConfig.number_of_months ||
                  rawGraphConfig.numberOfMonths ||
                  6,
                showXAxis:
                  rawGraphConfig.show_x_axis ??
                  rawGraphConfig.showXAxis ??
                  true,
                showYAxis:
                  rawGraphConfig.show_y_axis ??
                  rawGraphConfig.showYAxis ??
                  true,
                showValues:
                  rawGraphConfig.show_values ??
                  rawGraphConfig.showValues ??
                  true,
              };
            }

            canvasComponents.push({
              id: layoutData.id && layoutData.id !== "NaN" ? layoutData.id : key,
              componentId: componentDef.id,
              type: componentDef.componentName,
              alias: componentDef.componentAlias || alias,
              x: layoutData.x || 0,
              y: layoutData.y || 0,
              // API uses w/h or width/height
              width:
                layoutData.w ||
                layoutData.width ||
                componentDef.defaultSize?.width ||
                300,
              height:
                layoutData.h ||
                layoutData.height ||
                componentDef.defaultSize?.height ||
                150,
              zIndex: layoutData.zIndex || 1,
              rotation: layoutData.rotation || 0,
              isVisible: true,
              isLocked: false,
              content: sideConfig.content || sideConfig.content_text || "",
              imageUrl:
                sideConfig.imageUrl ||
                sideConfig.image_url ||
                componentDef.imageUrl ||
                "",
              graphConfig: graphConfig,
              fields: fields,
              style:
                sideConfig.style ||
                componentDef.style || {
                  backgroundColor: "#ffffff",
                  borderColor: "#e5e7eb",
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderRadius: 0,
                  padding: 10,
                  fontSize: 14,
                  color: "#374151",
                  textAlign: "left",
                },
            });
        }
      }

      setComponents(canvasComponents);

      console.log(
        `✅ Loaded ${canvasComponents.length} components for ${side} side`,
      );
    } else {
      // Clear components if no layout source found
      setComponents([]);
    }
  };

  // Initial data loading
  useEffect(() => {
    if (templateData?.result) {
      if (id) {
        // Editing existing configured template
        processConfiguredTemplateData(templateDataDetail);
      }

      // New template - set available templates
      if (templateData.result.availableTemplates) {
        setAvailableTemplates(templateData.result.availableTemplates);
      } else if (Array.isArray(templateData.result)) {
        // If API returns array of templates
        setAvailableTemplates(
          templateData.result.map((t: any) => ({
            id: t.id,
            name: t.templateName || t.template_name,
            alias: t.templateAlias || t.template_alias,
          })),
        );
      }
    }
  }, [templateData, id, templateDataDetail]);

  // Refresh layout when side changes (for multi-sided templates)
  useEffect(() => {
    if (templateDataDetail) {
      processCanvasLayout(templateDataDetail, availableComponents, activeSide);
    }
  }, [activeSide, templateDataDetail, availableComponents]);

  // Transform components to save payload
  const transformToSavePayload = () => {
    // New format: canvasLayout is an array, componentConfig is grouped by alias
    const canvasLayoutList: any[] = [];
    const groupedComponentConfig: any = {};

    components.forEach((component) => {
      canvasLayoutList.push({
        component_alias:
          component.alias || component.type.toLowerCase().replace(/\s+/g, "_"),
        x: component.x,
        y: component.y,
        width: component.width,
        height: component.height,
        zIndex: component.zIndex,
        rotation: component.rotation,
      });

      // Group configuration by alias (as per user request)
      const alias =
        component.alias || component.type.toLowerCase().replace(/\s+/g, "_");

      // Get current config from configurationManager or state
      const config = componentConfig[component.id] || {
        style: component.style,
        fields: component.fields,
      };

      groupedComponentConfig[alias] = {
        style: config.style || component.style,
        available_fields: (config.fields || component.fields).map((f: any) => ({
          field_key: f.fieldName,
          field_label: f.label,
          is_active: f.isActive,
          show_label: f.showLabel,
          show_value: f.showValue,
          order: f.order,
        })),
      };
    });

    return {
      remoteUtilityId: getRemoteUtilityId(),
      configuredTemplateName: templateName,
      baseTemplate: 22,
      isActive: true,
      canvasLayout: canvasLayoutList,
      componentConfig: groupedComponentConfig,
    };
  };

  const saveTemplate = async (customPayload?: any) => {
    if (!templateName.trim() && !customPayload) {
      throw new Error("Template name is required");
    }

    if (components.length === 0 && !customPayload) {
      throw new Error("Please add at least one component to the canvas");
    }

    const payload = customPayload || transformToSavePayload();
    const result = await saveTemplateMutation(payload);

    // Update templateId after successful save
    if (result?.id) {
      setTemplateId(result.id);
    }

    refetch();
  };

  const updateTemplate = async (customPayload?: any) => {
    if (!templateId) {
      throw new Error("No template ID found to update");
    }

    if (!templateName.trim() && !customPayload) {
      throw new Error("Template name is required");
    }

    const payload = customPayload || transformToSavePayload();
    await updateTemplateMutation({ id: templateId.toString(), payload });
    refetch();
  };

  const error =
    queryError?.message || saveError?.message || updateError?.message || null;

  return {
    components,
    setComponents,
    availableComponents,
    availableTemplates,
    templateName,
    setTemplateName,
    isLoading,
    isLoadingDetail,
    error,
    queryErrorDetail,
    saveTemplate,
    isSaving,
    updateTemplate,
    isUpdating,
    templateId,
    baseTemplateId,
    refetch,
    loadTemplate,
    componentConfig,
    setComponentConfig,
    isLoadingBaseTemplate,
    activeSide,
    setActiveSide,
    rawDetailData: templateDataDetail,
    templateType,
    setTemplateType,
    category,
    setCategory,
    perforationEnabled,
    setPerforationEnabled,
    perforationY,
    setPerforationY,
    isDoubleSided,
    setIsDoubleSided,
  };
}
// hooks/useTemplateSystem.ts
/**
 * useTemplateSystem Hook
 *
 * Main hook that wraps all template system functionality.
 * Manages components, configuration, and rendering.
 */

import { useCallback } from "react";

import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  downloadFile,
  openPdfInNewTab,
  printPDFBlob,
} from "@shared/utils/utils";
import {
  APITemplateResponse,
  CanvasComponent,
  ComponentConfiguration,
  FieldDefinition,
  GraphConfig,
  NormalizedComponent,
} from "./types";
import {
  componentNormalizer,
  configurationManager,
  templateEngine,
} from "./utils";

export interface UseTemplateSystemReturn {
  frontComponents: NormalizedComponent[];
  backComponents: NormalizedComponent[];
  frontConfig: any;
  backConfig: any;
  activeSide: "front" | "back";
  isInitialized: boolean;
  error: string | null;
  initializeTemplate: (response: APITemplateResponse) => void;
  addComponent: (apiComponent: any, layout?: any) => string;
  updateComponent: (id: string, updates: Partial<NormalizedComponent>) => void;
  deleteComponent: (id: string) => void;
  updateField: (componentId: string, fieldName: string, updates: any) => void;
  toggleField: (componentId: string, fieldName: string) => void;
  toggleLabel: (componentId: string, fieldName: string) => void;
  reorderField: (
    componentId: string,
    fieldName: string,
    direction: "up" | "down",
  ) => void;
  updateComponentContent: (componentId: string, content: string) => void;
  updateComponentImageUrl: (componentId: string, imageUrl: string) => void;
  getFields: (componentId: string) => FieldDefinition[];
  renderComponent: (componentId: string, data?: any) => string;
  renderPreview: (
    data?: any,
    pageSize?: { width: number; height: number },
  ) => string;
  switchSide: (side: "front" | "back") => void;
  exportConfiguration: () => any;
  getStats: () => any;
  templateType: string;
  setTemplateType: (type: string) => void;
  category: string;
  setCategory: (category: string) => void;
  perforationEnabled: boolean;
  setPerforationEnabled: (enabled: boolean) => void;
  perforationY: number;
  setPerforationY: (y: number) => void;
}

export function useTemplateSystem(): UseTemplateSystemReturn {
  const [frontComponents, setFrontComponents] = useState<NormalizedComponent[]>(
    [],
  );
  const [backComponents, setBackComponents] = useState<NormalizedComponent[]>(
    [],
  );
  const [frontConfig, setFrontConfig] = useState<any>({});
  const [backConfig, setBackConfig] = useState<any>({});
  const [activeSide, setActiveSide] = useState<"front" | "back">("front");
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // New states for header redesign
  const [templateType, setTemplateType] = useState("");
  const [category, setCategory] = useState("");
  const [perforationEnabled, setPerforationEnabled] = useState(false);
  const [perforationY, setPerforationY] = useState(1000);

  const getCurrentComponents = useCallback(() => {
    return activeSide === "front" ? frontComponents : backComponents;
  }, [activeSide, frontComponents, backComponents]);

  const setCurrentComponents = useCallback(
    (components: NormalizedComponent[]) => {
      if (activeSide === "front") {
        setFrontComponents(components);
      } else {
        setBackComponents(components);
      }
    },
    [activeSide],
  );

  // CRITICAL FIX: Sync component fields with configurationManager
  const syncComponentFields = useCallback(() => {
    const components = getCurrentComponents();
    const updatedComponents = components.map((component) => {
      const latestFields = configurationManager.getFields(component.id);
      const latestContent = configurationManager.getContent(component.id);
      const latestImageUrl = configurationManager.getImageUrl(component.id);

      return {
        ...component,
        fields: latestFields,
        content: latestContent || component.content,
        imageUrl: latestImageUrl || component.imageUrl,
      };
    });
    setCurrentComponents(updatedComponents);
    setUpdateTrigger((prev) => prev + 1);
  }, [getCurrentComponents, setCurrentComponents]);

  // Process canvas layout helper for this hook's multi-side state
  const processLayoutToState = useCallback(
    (result: any, baseComponents: any[], side: "front" | "back") => {
      // Support for multiple structure formats
      let layoutSource = null;
      let configSource = null;

      if (result?.pages) {
        const sideData =
          side === "front" ? result.pages.front : result.pages.back;
        layoutSource = sideData?.layout;
        configSource = sideData?.componentConfig;
      } else {
        layoutSource =
          result.configured_sequence ||
          result.canvasLayout ||
          result.defaultConfig;
        configSource = result.configuration_json || result.componentConfig;
      }

      if (layoutSource) {
        const canvasComponents: NormalizedComponent[] = [];
        const entries = Array.isArray(layoutSource)
          ? layoutSource.map((item, idx) => [item.id || `comp-${idx}`, item])
          : Object.entries(layoutSource);

        for (const [key, layout] of entries) {
          const layoutData = layout as any;
          const alias =
            layoutData.componentAlias ||
            layoutData.component_alias ||
            layoutData.alias;

          // Find component definition from base components
          const componentDef = baseComponents.find(
            (c: any) =>
              (layoutData.componentId && c.id === layoutData.componentId) ||
              (alias && c.componentAlias === alias),
          );

          

          if (componentDef) {
            console.log("found component", componentDef);
            const normalizedAlias = alias?.replace(/_([a-z])/g, (g) =>
              g[1].toUpperCase(),
            );
            const snakeAlias = alias?.replace(
              /([A-Z])/g,
              (g) => `_${g[0].toLowerCase()}`,
            );

            const sideConfig =
              configSource?.[alias] ||
              configSource?.[normalizedAlias] ||
              configSource?.[snakeAlias] ||
              configSource?.[key] ||
              {};

            // 1. Normalize all available fields first to obtain consistent 'fieldName'
            const rawFields =
              componentDef.configuration?.availableFields ||
              componentDef.configuration?.available_fields ||
              componentDef.availableFields ||
              componentDef.available_fields ||
              componentDef.fields ||
              [];

            let fields: FieldDefinition[] = (
              Array.isArray(rawFields) ? rawFields : []
            ).map((f: any, idx: number) =>
              componentNormalizer.normalizeField(f, idx),
            );

            const fieldVisibility =
              sideConfig.field_visibility || sideConfig.fieldVisibility;
            const fieldLabels =
              sideConfig.field_labels || sideConfig.fieldLabels;
            const fieldOrder = sideConfig.field_order || sideConfig.fieldOrder;

            // 2. Apply configurations using robust key lookups
            fields = fields.map((f) => {
              const snakeName = f.fieldName.replace(
                /([A-Z])/g,
                (g) => `_${g[0].toLowerCase()}`,
              );
              const camelName = f.fieldName.replace(/_([a-z])/g, (g) =>
                g[1].toUpperCase(),
              );

              // Try all variations to find visibility/label
              const visibilityValue =
                fieldVisibility?.[f.fieldName] ??
                fieldVisibility?.[snakeName] ??
                fieldVisibility?.[camelName];

              const labelValue =
                fieldLabels?.[f.fieldName] ??
                fieldLabels?.[snakeName] ??
                fieldLabels?.[camelName];

              return {
                ...f,
                isActive: visibilityValue !== false,
                label: labelValue || f.label,
              };
            });

            // 3. Apply ordering
            if (fieldOrder && Array.isArray(fieldOrder)) {
              fields = fields.sort((a, b) => {
                const snakeA = a.fieldName.replace(
                  /([A-Z])/g,
                  (g) => `_${g[0].toLowerCase()}`,
                );
                const camelA = a.fieldName.replace(/_([a-z])/g, (g) =>
                  g[1].toUpperCase(),
                );
                const snakeB = b.fieldName.replace(
                  /([A-Z])/g,
                  (g) => `_${g[0].toLowerCase()}`,
                );
                const camelB = b.fieldName.replace(/_([a-z])/g, (g) =>
                  g[1].toUpperCase(),
                );

                const getIndex = (
                  fName: string,
                  sName: string,
                  cName: string,
                ) => {
                  const idx = fieldOrder.indexOf(fName);
                  if (idx > -1) return idx;
                  const sIdx = fieldOrder.indexOf(sName);
                  if (sIdx > -1) return sIdx;
                  return fieldOrder.indexOf(cName);
                };

                const orderA = getIndex(a.fieldName, snakeA, camelA);
                const orderB = getIndex(b.fieldName, snakeB, camelB);

                if (orderA > -1 && orderB > -1) return orderA - orderB;
                if (orderA > -1) return -1;
                if (orderB > -1) return 1;
                return 0;
              });
            }

            // Handle Graph Config if present
            const rawGraphConfig =
              sideConfig.graph_config || sideConfig.graphConfig;
            let graphConfig: GraphConfig | undefined = undefined;
            if (rawGraphConfig) {
              graphConfig = {
                utilityService:
                  rawGraphConfig.utility_service ||
                  rawGraphConfig.utilityService ||
                  "Gas",
                numberOfMonths:
                  rawGraphConfig.number_of_months ||
                  rawGraphConfig.numberOfMonths ||
                  6,
                showXAxis:
                  rawGraphConfig.show_x_axis ??
                  rawGraphConfig.showXAxis ??
                  true,
                showYAxis:
                  rawGraphConfig.show_y_axis ??
                  rawGraphConfig.showYAxis ??
                  true,
                showValues:
                  rawGraphConfig.show_values ??
                  rawGraphConfig.showValues ??
                  true,
              };
            }

            canvasComponents.push({
              id:
                layoutData.id && layoutData.id !== "NaN" ? layoutData.id : key,
              componentId: componentDef.id,
              type: componentDef.componentName,
              alias: componentDef.componentAlias || alias,
              x: layoutData.x || 0,
              y: layoutData.y || 0,
              width:
                layoutData.w ||
                layoutData.width ||
                componentDef.defaultSize?.width ||
                300,
              height:
                layoutData.h ||
                layoutData.height ||
                componentDef.defaultSize?.height ||
                150,
              zIndex: layoutData.zIndex || 1,
              rotation: layoutData.rotation || 0,
              isVisible: true,
              isLocked: false,
              originalHtml: componentDef.htmlData || "",
              templateHtml: componentDef.htmlData || "",
              fields: fields,
              content: sideConfig.content || sideConfig.content_text || "",
              imageUrl:
                sideConfig.imageUrl ||
                sideConfig.image_url ||
                componentDef.imageUrl ||
                "",
              graphConfig: graphConfig,
              style:
                sideConfig.style ||
                componentDef.style || {
                  backgroundColor: "#ffffff",
                  borderColor: "#e5e7eb",
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderRadius: 0,
                  padding: 10,
                  fontSize: 14,
                  color: "#374151",
                  textAlign: "left",
                },
            });
          }
        }

        if (side === "front") {
          setFrontComponents(canvasComponents);
          setFrontConfig(configSource || {});
        } else {
          setBackComponents(canvasComponents);
          setBackConfig(configSource || {});
        }

        return canvasComponents;
      }
      return [];
    },
    [],
  );

  const initializeTemplate = useCallback((response: APITemplateResponse) => {
    try {
      console.log("📦 Initializing template system...", response);
      setFrontComponents([]);
      setBackComponents([]);
      setFrontConfig({});
      setBackConfig({});
      configurationManager.clear();

      // Initialize new fields with snake_case support from API
      const res = response as any;
      const templateTypeVal = res.template_type || res.templateType || "";
      const categoryVal = res.category || "";

      if (templateTypeVal) setTemplateType(templateTypeVal);
      if (categoryVal) setCategory(categoryVal);

      // Handle perforation line with robust property mapping
      const perfLine = res.perforation_line || res.perforationLine;
      if (perfLine) {
        setPerforationEnabled(!!perfLine.enabled);
        setPerforationY(perfLine.y || perfLine.height || 0);
      }

      if (response.pages) {
        const frontComps = processLayoutToState(
          response,
          response.components || [],
          "front",
        );
        const backComps = processLayoutToState(
          response,
          response.components || [],
          "back",
        );
        configurationManager.initialize([...frontComps, ...backComps]);
      } else {
        const comps = processLayoutToState(
          response,
          response.components || [],
          "front",
        );
        configurationManager.initialize(comps);
      }

      setIsInitialized(true);
      setError(null);
      console.log("✅ Template system initialized");
    } catch (err: any) {
      console.error("❌ Initialization error:", err);
      setError(err.message);
      setIsInitialized(false);
    }
  }, []);

  const addComponent = useCallback(
    (apiComponent: any, layout?: any) => {
      try {
        const normalized = componentNormalizer.normalize(apiComponent, layout);
        const components = getCurrentComponents();
        configurationManager.addComponent(normalized);
        setCurrentComponents([...components, normalized]);
        console.log(`✅ Added component: ${normalized.id}`);
        return normalized.id;
      } catch (err: any) {
        console.error("❌ Error adding component:", err);
        setError(err.message);
        return "";
      }
    },
    [getCurrentComponents, setCurrentComponents],
  );

  const updateComponent = useCallback(
    (id: string, updates: Partial<NormalizedComponent>) => {
      try {
        const components = getCurrentComponents();
        const updatedComponents = components.map((comp) =>
          comp.id === id ? { ...comp, ...updates } : comp,
        );
        setCurrentComponents(updatedComponents);
        setUpdateTrigger((prev) => prev + 1);
      } catch (err: any) {
        console.error("❌ Error updating component:", err);
      }
    },
    [getCurrentComponents, setCurrentComponents],
  );

  const deleteComponent = useCallback(
    (id: string) => {
      try {
        const components = getCurrentComponents();
        const filtered = components.filter((comp) => comp.id !== id);
        configurationManager.removeComponent(id);
        setCurrentComponents(filtered);
        console.log(`✅ Deleted component: ${id}`);
      } catch (err: any) {
        console.error("❌ Error deleting component:", err);
      }
    },
    [getCurrentComponents, setCurrentComponents],
  );

  // FIXED: updateField now syncs component state
  const updateField = useCallback(
    (componentId: string, fieldName: string, updates: any) => {
      try {
        configurationManager.updateField(componentId, fieldName, updates);
        syncComponentFields(); // CRITICAL FIX

        const updatedConfig = configurationManager.getAllConfigs();
        if (activeSide === "front") {
          setFrontConfig(updatedConfig);
        } else {
          setBackConfig(updatedConfig);
        }

        console.log(`✅ Updated field: ${fieldName}`, updates);
      } catch (err: any) {
        console.error("❌ Error updating field:", err);
      }
    },
    [activeSide, syncComponentFields],
  );

  // FIXED: toggleField now syncs component state
  const toggleField = useCallback(
    (componentId: string, fieldName: string) => {
      try {
        configurationManager.toggleFieldActive(componentId, fieldName);
        syncComponentFields(); // CRITICAL FIX

        const updatedConfig = configurationManager.getAllConfigs();
        if (activeSide === "front") {
          setFrontConfig(updatedConfig);
        } else {
          setBackConfig(updatedConfig);
        }

        console.log(`✅ Toggled field: ${fieldName}`);
      } catch (err: any) {
        console.error("❌ Error toggling field:", err);
      }
    },
    [activeSide, syncComponentFields],
  );

  // FIXED: toggleLabel now syncs component state
  const toggleLabel = useCallback(
    (componentId: string, fieldName: string) => {
      try {
        configurationManager.toggleLabelVisibility(componentId, fieldName);
        syncComponentFields(); // CRITICAL FIX

        const updatedConfig = configurationManager.getAllConfigs();
        if (activeSide === "front") {
          setFrontConfig(updatedConfig);
        } else {
          setBackConfig(updatedConfig);
        }

        console.log(`✅ Toggled label: ${fieldName}`);
      } catch (err: any) {
        console.error("❌ Error toggling label:", err);
      }
    },
    [activeSide, syncComponentFields],
  );

  // FIXED: reorderField now syncs component state
  const reorderField = useCallback(
    (componentId: string, fieldName: string, direction: "up" | "down") => {
      try {
        configurationManager.reorderField(componentId, fieldName, direction);
        syncComponentFields(); // CRITICAL FIX

        const updatedConfig = configurationManager.getAllConfigs();
        if (activeSide === "front") {
          setFrontConfig(updatedConfig);
        } else {
          setBackConfig(updatedConfig);
        }

        console.log(`✅ Reordered field: ${fieldName} ${direction}`);
      } catch (err: any) {
        console.error("❌ Error reordering field:", err);
      }
    },
    [activeSide, syncComponentFields],
  );

  const updateComponentContent = useCallback(
    (componentId: string, content: string) => {
      try {
        configurationManager.updateContent(componentId, content);
        syncComponentFields();

        const updatedConfig = configurationManager.getAllConfigs();
        if (activeSide === "front") {
          setFrontConfig(updatedConfig);
        } else {
          setBackConfig(updatedConfig);
        }

        console.log(`✅ Updated component content: ${componentId}`);
      } catch (err: any) {
        console.error("❌ Error updating component content:", err);
      }
    },
    [activeSide, syncComponentFields],
  );

  const updateComponentImageUrl = useCallback(
    (componentId: string, imageUrl: string) => {
      try {
        configurationManager.updateImageUrl(componentId, imageUrl);
        syncComponentFields();

        const updatedConfig = configurationManager.getAllConfigs();
        if (activeSide === "front") {
          setFrontConfig(updatedConfig);
        } else {
          setBackConfig(updatedConfig);
        }

        console.log(`✅ Updated component image URL: ${componentId}`);
      } catch (err: any) {
        console.error("❌ Error updating component image URL:", err);
      }
    },
    [activeSide, syncComponentFields],
  );

  const getFields = useCallback(
    (componentId: string): FieldDefinition[] => {
      return configurationManager.getFields(componentId);
    },
    [updateTrigger],
  );

  // FIXED: renderComponent always uses latest fields
  const renderComponent = useCallback(
    (componentId: string, data?: any): string => {
      const components = getCurrentComponents();
      let component = components.find((c) => c.id === componentId);

      if (!component) {
        console.warn(`Component ${componentId} not found`);
        return "";
      }

      // CRITICAL FIX: Get latest fields from configurationManager
      const latestFields = configurationManager.getFields(componentId);
      component = {
        ...component,
        fields: latestFields,
      };

      return templateEngine.render(component, data);
    },
    [getCurrentComponents, updateTrigger],
  );

  const renderPreview = useCallback(
    (data?: any, pageSize?: { width: number; height: number }): string => {
      let components = getCurrentComponents();

      // CRITICAL FIX: Update all components with latest fields
      components = components.map((component) => ({
        ...component,
        fields: configurationManager.getFields(component.id),
      }));

      return templateEngine.renderPreview(components, data, pageSize);
    },
    [getCurrentComponents, updateTrigger],
  );

  const switchSide = useCallback((side: "front" | "back") => {
    setActiveSide(side);
    console.log(`✅ Switched to ${side} side`);
  }, []);

  const exportConfiguration = useCallback(() => {
    const formatLayout = (components: NormalizedComponent[]) => {
      return components.map((component) => ({
        id: component.id,
        component_alias:
          component.alias || component.type.toLowerCase().replace(/\s+/g, "_"),
        x: component.x,
        y: component.y,
        w: component.width,
        h: component.height,
      }));
    };

    const formatConfig = (components: NormalizedComponent[]) => {
      const config: any = {};
      components.forEach((component) => {
        const alias =
          component.alias || component.type.toLowerCase().replace(/\s+/g, "_");

        // Use the fields and style from configurationManager if available
        const latestFields = configurationManager.getFields(component.id);
        const fieldsToUse =
          latestFields.length > 0 ? latestFields : component.fields;

        // Separate fields into the required structure
        const field_order: string[] = [];
        const field_labels: Record<string, string> = {};
        const field_visibility: Record<string, boolean> = {};

        fieldsToUse.forEach((f) => {
          field_order.push(f.fieldName);
          field_labels[f.fieldName] = f.label;
          field_visibility[f.fieldName] = f.isActive;
        });

        config[alias] = {
          field_order,
          field_labels,
          field_visibility,
          style: component.style, // Keeping style as it's useful, even if not explicitly in the requested sample
          content: component.content,
          imageUrl: component.imageUrl,
          // Add graphConfig for graph components
          ...(component.graphConfig && {
            graph_config: {
              utility_service: component.graphConfig.utilityService,
              number_of_months: component.graphConfig.numberOfMonths,
              show_x_axis: component.graphConfig.showXAxis,
              show_y_axis: component.graphConfig.showYAxis,
              show_values: component.graphConfig.showValues,
            },
          }),
        };
      });
      return config;
    };

    return {
      front: {
        canvasLayout: formatLayout(frontComponents),
        componentConfig: formatConfig(frontComponents),
      },
      back: {
        canvasLayout: formatLayout(backComponents),
        componentConfig: formatConfig(backComponents),
      },
      perforationLine: {
        enabled: perforationEnabled,
        height: perforationY,
        x: 0,
        y: perforationY,
      },
      templateType,
      category,
    };
  }, [
    frontComponents,
    backComponents,
    perforationEnabled,
    perforationY,
    templateType,
    category,
  ]);

  const getStats = useCallback(() => {
    const stats = configurationManager.getStats();
    return {
      ...stats,
      frontComponents: frontComponents.length,
      backComponents: backComponents.length,
    };
  }, [frontComponents, backComponents]);

  return {
    frontComponents,
    backComponents,
    frontConfig,
    backConfig,
    activeSide,
    isInitialized,
    error,
    initializeTemplate,
    addComponent,
    updateComponent,
    deleteComponent,
    updateField,
    toggleField,
    toggleLabel,
    reorderField,
    updateComponentContent,
    updateComponentImageUrl,
    getFields,
    renderComponent,
    renderPreview,
    switchSide,
    exportConfiguration,
    getStats,
    templateType,
    setTemplateType,
    category,
    setCategory,
    perforationEnabled,
    setPerforationEnabled,
    perforationY,
    setPerforationY,
  };
}

export function useTemplateChoices(filter?: "template_type" | "template") {
  return useSmartQuery(QueryKeyFactory.module.bx.templateChoices(filter), () =>
    getTemplateChoices(filter),
  );
}

export const useTemplateSummary = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billTemplate.summary(), getRemoteUtilityId()],
    () => getTemplateSummary(),
  );
};

export const useTemplateList = (status?: string, searchData?: string) => {
  const mapStatusToBoolean = (status?: string): string | undefined => {
    switch (status) {
      case "active":
        return "true";
      case "inactive":
        return "false";
      case "all":
      default:
        return undefined;
    }
  };

  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billTemplate.templateList(),
      getRemoteUtilityId(),
      status,
      searchData,
    ],
    () => getTemplateList(mapStatusToBoolean(status), searchData),
  );
};

export const useCloneTemplate = () => {
  return useSmartMutation(
    async (id: string | number) => {
      return cloneBilltemplate(id);
    },
    {
      onSuccess: (response, variables) => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeyFactory.module.bx.billTemplate.templateList()],
        });
      },
    },
  );
};

export const useChangeTemplateStatus = () => {
  return useSmartMutation(
    async ({ id, isActive }: { id: string | number; isActive: boolean }) => {
      return (
        changeTemplateStatus(id, isActive),
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: [QueryKeyFactory.module.bx.billTemplate.templateList()],
            });
          },
        }
      );
    },
  );
};

export const useDeleteTemplate = () => {
  return useSmartMutation(async (id: string | number) => {
    return deleteTemplate(id);
  });
};

export const useTemplateOptions = () => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billTemplate.templateOptions(),
      getRemoteUtilityId(),
    ],
    () => getTemplateOptions(),
  );
};

export const useTemplateDetail = (id: string) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.billTemplate.templateDetail(id),
    () => getTemplateDetail(id, remoteUtilityId),
  );
};

export const useArchiveTemplate = () =>
  useSmartMutation((id: string) => archiveTemplate(id), {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["bx", "billing", "list"] });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.bx.canvasTemplate.details(variables),
      });
    },
  });

export const useTemplateUsage = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.billTemplate.templateUsage(id),
    () => getTemplateUsage(id),
  );
};

export const useActivityLog = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.billTemplate.templateActivityLog(id),
    () => getTemplateActivityLog(id),
  );
};

export const useDownloadBillTemplate = () => {
  return useSmartMutation(
    async ({
      id,
      filename = "template.pdf",
    }: {
      id: string;
      filename?: string;
    }) => {
      const blob = await downloadBillTemplate(id);
      downloadFile(blob, filename);
      return blob;
    },
  );
};

export const useViewBillTemplate = () => {
  return useSmartMutation(async (id: string) => {
    const blob = await downloadBillTemplate(id);
    openPdfInNewTab(blob);
    return blob;
  });
};

export const usePrintBillTemplate = () => {
  return useSmartMutation(async (id: string) => {
    const blob = await downloadBillTemplate(id);
    printPDFBlob(blob);
    return blob;
  });
};
